/**
 * Cities Transformers
 * Maps between API DTOs and Domain Models
 */

import { mergeLocalizationsStrict } from '@ufc/core'
import type { LocalizedText, StrapiLocalizedDto } from '@ufc/core'
import type {
  City,
  CityListItem,
  CitiesFilter,
  PaginatedCities,
  CreateCityRequest,
  UpdateCityRequest,
  RegionSummary,
  CountrySummary,
} from '../domain/types'
import type {
  CityDto,
  CreateCityApiRequest,
  UpdateCityApiRequest,
  CityListResponse,
  RegionDto,
  CountryDto,
} from './api-types'

function toLocalizedText(dto: StrapiLocalizedDto, field: string): LocalizedText {
  return mergeLocalizationsStrict(dto, field) as LocalizedText
}

// ===========================================
// RELATED ENTITY TRANSFORMERS
// ===========================================

/**
 * Transform Region DTO to domain summary
 */
function transformRegionDtoToDomain(dto: RegionDto): RegionSummary {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    isActive: dto.isActive ?? true,
  }
}

/**
 * Transform Country DTO to domain summary
 */
function transformCountryDtoToDomain(dto: CountryDto): CountrySummary {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    isActive: dto.isActive ?? true,
  }
}

// ===========================================
// DTO → DOMAIN TRANSFORMERS
// ===========================================

/**
 * Transform API DTO to Domain Model
 * Called after fetching from API
 */
export function transformDtoToDomain(dto: CityDto): City {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: toLocalizedText(dto, 'name'),
    stateProvince: dto.stateProvince != null ? toLocalizedText(dto, 'stateProvince') : undefined,
    salesDistrict: dto.salesDistrict ?? undefined,
    isActive: dto.isActive ?? true,
    // Transform relations
    region: dto.region ? transformRegionDtoToDomain(dto.region) : undefined,
    country: dto.country ? transformCountryDtoToDomain(dto.country) : undefined,
    // Timestamps
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

/**
 * Transform API DTO to CityListItem (UI-friendly)
 */
export function transformDtoToListItem(dto: CityDto): CityListItem {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: toLocalizedText(dto, 'name'),
    stateProvince: dto.stateProvince != null ? toLocalizedText(dto, 'stateProvince') : undefined,
    salesDistrict: dto.salesDistrict ?? undefined,
    isActive: dto.isActive ?? true,
    // Region info
    regionId: dto.region ? String(dto.region.id) : undefined,
    regionCode: dto.region?.code,
    regionName: dto.region?.name,
    // Country info
    countryId: dto.country ? String(dto.country.id) : undefined,
    countryCode: dto.country?.code,
    countryName: dto.country?.name,
    // Timestamps
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  }
}

/**
 * Transform list of DTOs to Domain Models
 */
export function transformDtoListToDomain(dtos: CityDto[]): City[] {
  return dtos.map(transformDtoToDomain)
}

/**
 * Transform list of DTOs to CityListItems
 */
export function transformDtoListToListItems(dtos: CityDto[]): CityListItem[] {
  return dtos.map(transformDtoToListItem)
}

/**
 * Transform paginated API response to domain format
 */
export function transformListResponseToDomain(response: CityListResponse): PaginatedCities {
  return {
    data: transformDtoListToListItems(response.data),
    pagination: response.meta.pagination,
  }
}

// ===========================================
// DOMAIN → DTO TRANSFORMERS
// ===========================================

/**
 * Transform Domain CreateCityRequest to API request
 */
export function transformToCreateApiRequest(data: CreateCityRequest): CreateCityApiRequest {
  const request: CreateCityApiRequest = {
    code: data.code,
    name: data.name.en,
    stateProvince: data.stateProvince?.en,
    isActive: data.isActive ?? true,
    country: data.countryId,
  }

  if (data.salesDistrict !== undefined) {
    request.salesDistrict = data.salesDistrict
  }

  if (data.regionId) {
    request.region = data.regionId
  }

  return request
}

/**
 * Transform Domain UpdateCityRequest to API request
 */
export function transformToUpdateApiRequest(data: UpdateCityRequest): UpdateCityApiRequest {
  const request: UpdateCityApiRequest = {}

  if (data.name !== undefined) {
    request.name = data.name.en
  }

  if (data.stateProvince !== undefined) {
    request.stateProvince = data.stateProvince.en
  }

  if (data.salesDistrict !== undefined) {
    request.salesDistrict = data.salesDistrict
  }

  if (data.isActive !== undefined) {
    request.isActive = data.isActive
  }

  if (data.regionId !== undefined) {
    request.region = data.regionId
  }

  if (data.countryId !== undefined) {
    request.country = data.countryId
  }

  return request
}

// ===========================================
// FILTER TRANSFORMERS
// ===========================================

/**
 * Transform Domain Filter to Strapi Query Params
 */
export function transformFilterToQueryParams(
  filter: CitiesFilter
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {}

  // Pagination
  if (filter.page !== undefined) {
    params['pagination[page]'] = filter.page
  }
  if (filter.pageSize !== undefined) {
    params['pagination[pageSize]'] = filter.pageSize
  }

  // Filters
  if (filter.isActive !== undefined && filter.isActive !== 'all') {
    params['filters[isActive][$eq]'] = filter.isActive
  }

  if (filter.search) {
    params['filters[$or][0][name][$containsi]'] = filter.search
    params['filters[$or][1][code][$containsi]'] = filter.search
  }

  if (filter.regionId) {
    params['filters[region][documentId][$eq]'] = filter.regionId
  }

  // Sorting
  params['sort'] = 'name:asc'

  return params
}

// ===========================================
// FORM DATA TRANSFORMERS
// ===========================================

/**
 * Transform form data to CreateCityRequest
 */
export function transformFormToCreateRequest(
  formData: {
    code: string
    name: LocalizedText
    stateProvince?: LocalizedText
    salesDistrict?: string
    isActive?: boolean
    regionId?: string
    countryId?: string
  }
): CreateCityRequest {
  return {
    code: formData.code,
    name: formData.name,
    stateProvince: formData.stateProvince,
    salesDistrict: formData.salesDistrict,
    isActive: formData.isActive ?? true,
    regionId: formData.regionId,
    countryId: formData.countryId,
  }
}

/**
 * Transform form data to UpdateCityRequest
 */
export function transformFormToUpdateRequest(
  formData: {
    name?: LocalizedText
    stateProvince?: LocalizedText
    salesDistrict?: string | null
    isActive?: boolean
    regionId?: string
    countryId?: string | null
  }
): UpdateCityRequest {
  return {
    name: formData.name,
    stateProvince: formData.stateProvince,
    salesDistrict: formData.salesDistrict,
    isActive: formData.isActive,
    regionId: formData.regionId,
    countryId: formData.countryId ?? undefined,
  }
}

/**
 * Transform City to form data for editing
 */
export function transformCityToFormData(
  city: City
): {
  code: string
  nameEn: string
  nameAr: string
  stateProvinceEn: string
  stateProvinceAr: string
  salesDistrict: string
  isActive: boolean
  regionId?: string
  countryId: string
} {
  return {
    code: city.code,
    nameEn: city.name.en,
    nameAr: city.name.ar ?? '',
    stateProvinceEn: city.stateProvince?.en ?? '',
    stateProvinceAr: city.stateProvince?.ar ?? '',
    salesDistrict: city.salesDistrict ?? '',
    isActive: city.isActive,
    regionId: city.region?.documentId ?? '',
    countryId: city.country?.documentId ?? '',
  }
}

// ===========================================
// UTILITY TRANSFORMERS
// ===========================================

/**
 * Extract document IDs from cities
 */
export function extractDocumentIds(cities: City[]): string[] {
  return cities.map(c => c.documentId)
}

/**
 * Create a lookup map by documentId
 */
export function createLookupMap(cities: City[]): Map<string, City> {
  return new Map(cities.map(c => [c.documentId, c]))
}
